import React, { useEffect, useRef, useState } from "react";

import { useIsSSR } from "react-aria";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { blue } from "../../../../utils/colors";
import StorageHandler from "../../../../utils/StorageHandler";
import SearchFieldLink from "../../../pages/GasStations/Finder/SearchFieldLink";
import DoubleShape from "../../DoubleShape";
import ZielIcon from "../../icons/ZielIcon";
import Image from "../../Image";
import Markdown from "../../Markdown";
import NewHeadline from "../../NewHeadline";

const FinderContainer = styled.div`
    color: ${({ theme }) => theme.colors.white.toString()};
    display: flex;
    flex-direction: column;
    max-width: 100vw;
    padding: 20px;
    min-height: 300px;
    justify-content: center;
    width: 500px;
    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) =>
        theme.breakpoints.from.extraLarge.css`
            width: unset;`}
    ${({ theme }) => theme.breakpoints.upTo.extraSmall.css`
        justify-content: flex-end;
        width: 500px;
    `}

    position: relative;
`;

const TextContainer = styled(Markdown)`
    font-weight: normal;
`;

const SearchFieldWrapper = styled.div`
    max-width: 250px;
    ${({ theme }) => theme.breakpoints.upTo.extraSmall.css`
        max-width: 210px;
    `}
`;

const MapContainer = styled.div`
    position: relative;
    left: -100px;
    width: 100%;
    height: 100%;
`;

const ZielIconContainer = styled.div`
    position: absolute;
    top: 3%;
    left: 35px;
    z-index: 1;
    display: flex;
    width: 50px;
    ${({ theme }) =>
        theme.breakpoints.from.small.css`
            left: 45px;`}
    ${({ theme }) =>
        theme.breakpoints.from.large.css`
            left: 30px;`}
    ${({ theme }) =>
        theme.breakpoints.from.extraLarge.css`
            left: 50px;`}
    svg {
        width: 100%;
        height: 100%;
    }
`;

const Img = css`
    width: 100%;
    height: 100%;
`;

const storage = new StorageHandler();

const Finder = ({ name, submit, text, types, image, properties }) => {
    const elementRef = useRef();
    const isSsr = useIsSSR();
    const [inViewport, setInViewport] = useState(false);
    const theme = useTheme();

    const rightContainerStyles = css`
        ${theme.breakpoints.upTo.small.css`
            margin-left: 20px;`}
    `;

    useEffect(() => {
        /**
         * When the component scrolls into the viewport
         * the google maps input will be rendered.
         */
        const onScroll = () => {
            if (!elementRef.current) {
                return;
            }

            const element = elementRef.current;
            const rect = element.getBoundingClientRect();
            const viewportHeight = Math.max(
                document.documentElement.clientHeight || 0,
                window.innerHeight || 0
            );

            if (viewportHeight - rect.top > 0 && !inViewport) {
                setInViewport(true);
            }
        };

        if (!isSsr && !inViewport) {
            window.addEventListener("scroll", onScroll, { passive: true });

            return () => {
                window.removeEventListener("scroll", onScroll);
            };
        }
    }, [elementRef, inViewport, isSsr]);

    return (
        <>
            <DoubleShape rightContainerStyles={rightContainerStyles}>
                <FinderContainer ref={elementRef}>
                    <NewHeadline color="white" light is="3">
                        {name}
                    </NewHeadline>
                    <>
                        {inViewport && (
                            <SearchFieldWrapper>
                                <SearchFieldLink
                                    types={types}
                                    properties={properties}
                                >
                                    {submit}
                                </SearchFieldLink>
                            </SearchFieldWrapper>
                        )}
                        <TextContainer>{text}</TextContainer>
                    </>
                </FinderContainer>
                <MapContainer>
                    <ZielIconContainer>
                        <ZielIcon color={blue} />
                    </ZielIconContainer>
                    {image?.src && <Image css={Img} {...image} />}
                </MapContainer>
            </DoubleShape>
        </>
    );
};

export default Finder;
